import { useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import Logout from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import SettingsIcon from '@mui/icons-material/Settings';
import { globalLogOut } from '@/lib/auth/authServices';
import { useGetCurrentUser } from '@/hooks/useGetCurrentUser';
import FRONTEND_ROUTES from '@/consts/frontendRoutes';
import { buildFullName } from '@/utils/helpers/commonHelpers';
import LicenseChip from '@/content/components/common/Chip/LicenseChip';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`,
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`,
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`,
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`,
);

const HeaderUserbox = () => {
  const { t } = useTranslation('common');
  const ref = useRef(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const theme = useTheme();
  const navigate = useNavigate();

  const { data: userData } = useGetCurrentUser();
  const userFullName = buildFullName(userData?.title, userData?.firstName, userData?.lastName);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const signoutHandler = () => {
    globalLogOut();
  };

  const profileHandler = () => {
    navigate(FRONTEND_ROUTES.PROFILE);
    handleClose();
  };
  const licenseHandler = () => {
    navigate(FRONTEND_ROUTES.PROFILE_LICENSE);
    handleClose();
  };

  const userPreferencesHandler = () => {
    navigate(FRONTEND_ROUTES.USER_PREFERENCES);
    handleClose();
  };

  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <UserBoxButton id="user-box-button" color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar variant="rounded" alt={`${userFullName}'s avatar`} src={userData?.avatar} />
        {!isMdDown && (
        <UserBoxText>
          <UserBoxLabel variant="body1">{userFullName}</UserBoxLabel>
          <LicenseChip license={userData?.license} />
        </UserBoxText>
        )}
        <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuUserBox
          sx={{
            display: 'flex',
            minWidth: 210,
          }}
        >
          <Avatar variant="rounded" alt={userFullName ?? 'profileAvatar'} src={userData?.avatar} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{userFullName}</UserBoxLabel>
            <LicenseChip license={userData?.license} />
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ mb: 0 }} />
        <Stack
          spacing={1}
          sx={{ p: 1 }}
        >
          <Button
            fullWidth
            variant="text"
            size="medium"
            startIcon={<AccountCircle />}
            onClick={profileHandler}
            sx={{
              justifyContent: 'flex-start',
              px: 2,
              py: 1,
            }}
          >
            { t('HEADER_USER_BOX.PROFILE')}
          </Button>
          <Button
            fullWidth
            variant="text"
            size="medium"
            startIcon={<SettingsIcon />}
            onClick={userPreferencesHandler}
            sx={{
              justifyContent: 'flex-start',
              px: 2,
              py: 1,
            }}
          >
            { t('HEADER_USER_BOX.SETTINGS')}
          </Button>
          <Button
            fullWidth
            variant="text"
            size="medium"
            startIcon={<LocalPoliceIcon />}
            onClick={licenseHandler}
            sx={{
              justifyContent: 'flex-start',
              px: 2,
              py: 1,
            }}
          >
            { t('HEADER_USER_BOX.YOUR_LICENSE')}
          </Button>

          <Button
            fullWidth
            variant="text"
            size="medium"
            startIcon={<Logout />}
            onClick={signoutHandler}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              px: 2,
              py: 1,
            }}
          >
            { t('HEADER_USER_BOX.SIGN_OUT')}
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

export default HeaderUserbox;
